'use client';

import React, { useEffect, useState, useRef, useCallback } from "react";
import { getNotificationBanner, NotificationBannerRes, NotificationItem, setNotificationRead, useNotificationUnreadNum } from "@/features/notifications/services";
import { NotificationEventStatus, NotificationEventType } from "@/features/notifications/constant";
import useIsLogin from "@/features/notifications/hooks/useIsLogin";
import { useSetAtom, useAtom } from 'jotai';
import { rechargeNotificationAtom } from '@/store/balance';
import NotifyBannerContainer, { TransactionNotify, GeneralNotify } from './notify-banner-container';
import { track } from '@/utils/track';
import { unreadCountInfoAtom } from '@/store/notifications';
import { useNotificationJump } from '../hooks/useNotificationJump';
import { useChangeSubscription } from '@/hooks/use-change-subscription';

type TimeoutID = ReturnType<typeof setTimeout>;

// 需求规定显示2.7s
const DISPLAY_DURATION = 2.7 * 1000;   // banner展示时长（可随时改）

export default function NotifyBanner({ }) {
  const { isLogin } = useIsLogin();
  const setRechargeNotification = useSetAtom(rechargeNotificationAtom);
  const [, setUnreadCountInfo] = useAtom(unreadCountInfoAtom);
  const [notifyDetail, setNotifyDetail] = useState<NotificationBannerRes>();
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const clearTimer = useRef<TimeoutID>(undefined);
  const renderTimer = useRef<TimeoutID>(undefined);
  const queueRef = useRef<NotificationBannerRes[]>([]);
  const processingRef = useRef(false);
  // const t = useTranslations('notifications');

  // 使用 Hook 获取未读数量，仅在用户登录时请求
  const { data: unreadNumData, refetch: refetchUnreadNum } = useNotificationUnreadNum({
    enabled: isLogin,
  });

  // 更新未读数量
  useEffect(() => {
    if (unreadNumData) {
      setUnreadCountInfo({
        allUnreadCount: unreadNumData.all_unread_count,
        platformUnreadCount: unreadNumData.platform_unread_count,
        personalUnreadCount: unreadNumData.personal_unread_count,
      });
    }
  }, [unreadNumData, setUnreadCountInfo]);

  const { jumpByEventType } = useNotificationJump((detail: NotificationItem | NotificationBannerRes) => {
    console.log('跳转完成', detail);
    doSomethingAfterJump(detail);
  });

  // 获取通知横幅（订阅模式，不再使用 setTimeout 递归）
  // Fetch notification banner (subscription mode, no longer using setTimeout recursion)
  const fetchBanner = useCallback(async () => {
    if (!isLogin) return;

    try {
      const res: NotificationBannerRes = await getNotificationBanner(false); // 非轮询请求
      console.log('notification', res);

      const { msg_id, event_type, content } = res || {} as NotificationBannerRes;

      // content为空的事件类型
      const isEmptyContentEventTypes = [
        NotificationEventType.CumulativeDeposit,
        NotificationEventType.AggregationMsg
      ];

      if (msg_id && event_type && (content || isEmptyContentEventTypes.includes(event_type))) {
        // ---- 推入队列（核心） ----
        queueRef.current.push(res);
        processQueue();  // 尝试开始显示

        refetchUnreadNum();
      }
    } catch (err) {
      console.error('banner error', err);
    }
  }, [isLogin, refetchUnreadNum]);

  // 订阅 notice 变化（通过统一的 change-check 轮询）
  // Subscribe to notice changes (through unified change-check polling)
  useChangeSubscription('notice', fetchBanner, isLogin);

  function processQueue() {
    if (processingRef.current) return;           // 正在展示消息，不能重入
    if (queueRef.current.length === 0) return;   // 没消息

    processingRef.current = true;

    const nextMsg = queueRef.current.shift()!;

    // 设置当前展示内容
    setNotifyDetail(nextMsg);
    setShouldRender(false);

    enableBodyPointerEvents();

    // 动画前延迟 1.3s
    clearTimeout(renderTimer.current);
    renderTimer.current = setTimeout(() => {
      setShouldRender(true);

      // ---- 充值动画（保持你的逻辑）----
      if (
        nextMsg.event_type === 2 &&
        nextMsg.event_status === NotificationEventStatus.Success &&
        nextMsg.currency && nextMsg.amount
      ) {
        setRechargeNotification(null);
        setTimeout(() => {
          setRechargeNotification({
            currency: nextMsg.currency,
            amount: nextMsg.amount,
            eventType: nextMsg.event_type,
            eventStatus: nextMsg.event_status,
            msgId: nextMsg.msg_id,
          });
        }, 0);
      }

      // ---- 展示 4 秒，从显示时刻开始计时 ----
      clearTimeout(clearTimer.current);
      clearTimer.current = setTimeout(async () => {
        setIsVisible(false);

        // 300ms 动画结束
        setTimeout(async () => {
          setNotifyDetail(undefined);
          setShouldRender(false);
          setIsVisible(true);

          processingRef.current = false;
          processQueue();  // 显示下一条

        }, 300);

      }, DISPLAY_DURATION);

    }, 1300);
  }



  async function readMsg(msgInfo: NotificationBannerRes) {
    // 只在登录时才允许设置已读
    if (!isLogin || !msgInfo?.msg_id) return;
    if (msgInfo.msg_id != '0') {
      await setNotificationRead({ id: msgInfo.msg_id });
    }
  }

  // 点击跳转处理
  function handleJump(e?: React.MouseEvent) {
    e?.stopPropagation();

    jumpByEventType(notifyDetail);

    trackEvent();

    handleClose();
  }

  // 处理跳转后的逻辑
  function doSomethingAfterJump(notifyDetail: NotificationItem | NotificationBannerRes) {
    readMsg(notifyDetail as NotificationBannerRes);
  }

  // 由于Drawer弹窗组件显示时会将body的pointer-events设置为none，导致点击抽屉外区域时无法触发点击事件，所以这里站内信横幅显示时手动设置pointer-events为auto
  function enableBodyPointerEvents() {
    if (typeof document === 'undefined') return;
    document.body.style.pointerEvents = 'auto';
  }

  // 数据埋点上报
  function trackEvent() {
    const { event_type, msg_id, msg_config_id } = notifyDetail || {} as NotificationBannerRes;
    // 数据埋点-只上报代理、VIP、平台消息、累充、rummy
    const trackTypes = [
      NotificationEventType.Proxy,
      NotificationEventType.VIP,
      NotificationEventType.Platform,
      NotificationEventType.Rummy,
      NotificationEventType.CumulativeDeposit
    ];
    if (trackTypes.includes(event_type)) {
      const messageTypeObj = {
        [NotificationEventType.Platform]: 'Platform',
        [NotificationEventType.Proxy]: 'Referral',
        [NotificationEventType.VIP]: 'VIP',
        [NotificationEventType.Rummy]: 'Rummy Tournament',
        [NotificationEventType.CumulativeDeposit]: 'Deposit & Spin'
      };
      const trackParams: Record<string, string> = {
        message_type: messageTypeObj[event_type],
        message_push_id: msg_id,
        display_format: 'Top Banner',
      };
      if (event_type === NotificationEventType.Platform) {
        trackParams.message_id = msg_config_id;
      }

      // 上报埋点
      track('inbox_click', trackParams);
    }
  }

  function handleClose() {
    setIsVisible(false);
    clearTimeout(clearTimer.current);
    clearTimeout(renderTimer.current);

    // 等待动画完成后再清理
    setTimeout(() => {
      setNotifyDetail(undefined);
      setShouldRender(false);
      setIsVisible(true);

      processingRef.current = false;
      processQueue();
    }, 300); // 与 transition 时长一致
  }

  // 初始加载通知（登录后延迟 10 秒首次获取）
  // Initial load notification (first fetch after 10s delay when logged in)
  useEffect(() => {
    if (isLogin) {
      const timer = setTimeout(() => {
        console.log('【first request notification banner】');
        fetchBanner();
      }, 10 * 1000);
      return () => clearTimeout(timer);
    }
  }, [isLogin, fetchBanner]);

  // 清理定时器
  // Cleanup timers
  useEffect(() => {
    return () => {
      clearTimeout(clearTimer.current);
      clearTimeout(renderTimer.current);
    };
  }, []);

  function getIsShowCloseButton(): boolean {
    const { event_type, event_sub_type, button_links } = notifyDetail || {} as NotificationBannerRes;

    // 有可能显示关闭按钮的事件类型组
    if (event_type === NotificationEventType.Platform) {
      if (Array.isArray(button_links) && button_links.length > 0) {
        return false;
      }
      return true;
    } else if (event_type === NotificationEventType.Proxy) {
      if (event_sub_type !== 1 && event_sub_type !== 4) {
        return true;
      }
      return false;
    } else if (event_type === NotificationEventType.AggregationMsg) {
      return true;
    }

    return false;
  }

  // 根据不同的 event_type 渲染不同的内容组件
  const renderContent = () => {
    if (!notifyDetail) return null;

    const { event_type, event_sub_type, currency, amount, event_status, content, msg_count, jump_params } = notifyDetail;

    // event_type: 2=充值, 3=提现 使用 TransactionNotify
    if (event_type === 2 || event_type === 3) {
      return (
        <TransactionNotify
          currency={currency || ''}
          amount={amount || ''}
          eventType={event_type}
          eventStatus={event_status}
        />
      );
    }

    // event_type: 1=平台消息, 4=代理佣金, 5=VIP 6=Rummy 7=累充转盘 使用 GeneralNotify
    return (
      <GeneralNotify
        eventType={event_type}
        eventSubType={event_sub_type}
        amount={amount}
        content={content}
        msgCount={msg_count}
        jumpParams={jump_params}
        currency={currency}
      />
    );
  };

  if (notifyDetail?.msg_id && shouldRender) {

    return (
      <NotifyBannerContainer
        isVisible={isVisible}
        showCloseButton={getIsShowCloseButton()}
        onBannerClick={handleJump}
        onCloseAction={handleClose}
      >
        <div className="flex-1">
          {renderContent()}
        </div>
      </NotifyBannerContainer>
    );
  }

  return <></>;
}
